import { useAsync } from 'react-use'
import useGlobalState from '../hooks/useGlobalState'

import useIsDevelopmentDomain from '../hooks/useIsDevelopmentDomain'
import DataFileUploadButton from './DataFileUploadButton'
import Dropdown from './Dropdown'
import Input from './Input'
import { getApiRoot } from '../utils/helpers'

function Item({
    label,
    children,
    disabled = false,
}: {
    label: string
    children: React.ReactNode
    disabled?: boolean
}) {
    return (
        <div
            className={`py-1 ${disabled ? 'pointer-events-none select-none opacity-20' : ''} inline-flex items-center`}
        >
            <div className="inline-flex min-w-32">{label}:&nbsp;</div>
            <div className="inline-flex grow">{children}</div>
        </div>
    )
}

const staticDataUrls = [
    '/static/highlighted-line-segment.json',
    '/static/barshapes.json',
    '/static/default-years.json',
    '/static/reference-lines-and-forecast-bars.json',
    '/static/semiannual.json',
    '/static/bar1.json',
    '/static/bar2.json',
    '/static/bar3.json',
    '/static/bar4.json',
    '/static/1_gci.json',
    '/static/2_components.json',
    '/static/3_incomesector.json',
    '/static/4_shareshifts.json',
    '/static/5_blocs.json',
    '/static/6_geopolitical.json',
    '/static/7_regionalization.json',
    '/static/8_diversification.json',
    '/static/new2.json',
    '/static/us-china.json',
    '/static/profile.json',
]

export default function Debugger() {
    const dataUrl = useGlobalState((state) => state.dataUrl)
    const dataVersion = useGlobalState((state) => state.dataVersion)
    const setState = useGlobalState((state) => state.setState)
    const setDataUrl = (dataUrl: string) => setState({ dataUrl })

    const isDev = useIsDevelopmentDomain()

    const state = useAsync(async () => {
        const apiRoot = getApiRoot(dataUrl)

        const response = await fetch(`${apiRoot}/processed-data/index.json`)
        if (!response.ok) {
            throw new Error('Failed to fetch dynamic data URLs')
        }
        const data: string[] = await response.json()

        const rootUrls = data.map(
            (name: string) => `${apiRoot}/processed-data/roots/${name}.json`
        )
        return rootUrls
    }, [])

    const dynamicDataUrls = state.loading ? [] : state.value

    // // For debugging: load the first dynamic URL automatically
    // const [firstDynamicUrl] = dynamicDataUrls
    // const prev = usePrevious(firstDynamicUrl)
    // useEffect(() => {
    //     if (!firstDynamicUrl) {
    //         return
    //     }
    //     if (firstDynamicUrl === prev) {
    //         return
    //     }
    //     setDataUrl(firstDynamicUrl)
    // }, [firstDynamicUrl, setDataUrl])

    const options = [...dynamicDataUrls!, ...staticDataUrls]

    return (
        <div className="mb-4 flex flex-col gap-4">
            {isDev && (
                <>
                    <Item label="Data URL">
                        <Dropdown
                            className="text-left"
                            value={dataUrl || ''}
                            options={options}
                            setValue={setDataUrl}
                            testId="data-url-selector"
                        />
                    </Item>
                    <Item label="Upload data file">
                        <DataFileUploadButton />
                    </Item>
                </>
            )}
            {!isDev && (
                <Item label="Data URL">
                    <Input value={dataUrl || ''} setValue={setDataUrl} />
                </Item>
            )}
            <Item label="Data Version">{dataVersion || '(not set)'}</Item>
        </div>
    )
}
